import { FC, ReactNode } from 'react';
import { formatTranslation, TranslationKey } from 'intl';
import { RichTextTranslationKeys } from 'intl/keys';
import { Box, IconButton, Divider, Tooltip } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  Code,
  Link as LinkIcon,
  FormatListNumbered,
  FormatListBulleted,
  KeyboardReturn,
} from '@mui/icons-material';
import { Editor } from '@tiptap/react';
import { useIntl } from 'react-intl';
import { toolbarDividerSx, toolbarIconSx, toolbarSx } from './styles';
import { TextColorButtons } from './TextColorButtons';
import { RichTextVariant } from './types';
import { RICH_TEXT_VARIANTS } from './model';

interface RichTextToolbarProps {
  editor: Editor | null;
  disabled?: boolean;
  error?: boolean;
  /** `inline`: text formatting and colour only (a one-line value). */
  variant?: RichTextVariant;
}

interface ToolbarButton {
  icon: ReactNode;
  action: () => void;
  isActive: boolean;
  titleId: TranslationKey;
}

export const RichTextToolbar: FC<RichTextToolbarProps> = ({ editor, disabled, error, variant = 'block' }) => {
  const intl = useIntl();

  if (!editor) {
    return null;
  }

  const renderButtons = (buttons: ToolbarButton[]): ReactNode =>
    buttons.map((btn) => (
      <Tooltip key={btn.titleId} title={formatTranslation(intl, btn.titleId)}>
        <span>
          <IconButton
            onClick={btn.action}
            disabled={disabled}
            size="small"
            color={btn.isActive ? 'primary' : 'default'}
          >
            {btn.icon}
          </IconButton>
        </span>
      </Tooltip>
    ));

  const textFormatButtons: ToolbarButton[] = [
    {
      icon: <FormatBold sx={toolbarIconSx} />,
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: editor.isActive('bold'),
      titleId: RichTextTranslationKeys.richtextBold,
    },
    {
      icon: <FormatItalic sx={toolbarIconSx} />,
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: editor.isActive('italic'),
      titleId: RichTextTranslationKeys.richtextItalic,
    },
    {
      icon: <FormatUnderlined sx={toolbarIconSx} />,
      action: () => editor.chain().focus().toggleUnderline().run(),
      isActive: editor.isActive('underline'),
      titleId: RichTextTranslationKeys.richtextUnderline,
    },
    {
      icon: <FormatStrikethrough sx={toolbarIconSx} />,
      action: () => editor.chain().focus().toggleStrike().run(),
      isActive: editor.isActive('strike'),
      titleId: RichTextTranslationKeys.richtextStrikethrough,
    },
  ];

  const insertButtons: ToolbarButton[] = [
    {
      icon: <Code sx={toolbarIconSx} />,
      action: () => editor.chain().focus().toggleCode().run(),
      isActive: editor.isActive('code'),
      titleId: RichTextTranslationKeys.richtextCode,
    },
    {
      icon: <LinkIcon sx={toolbarIconSx} />,
      action: () => {
        const url = window.prompt(formatTranslation(intl, RichTextTranslationKeys.richtextLinkPrompt));
        if (!url) return;
        
        const trimmedUrl = url.trim();
        if (!trimmedUrl) return;

        const { from, to } = editor.state.selection;
        const hasSelection = from !== to;

        if (hasSelection) {
          editor.chain().focus().setLink({ href: trimmedUrl }).run();
        } else {
          editor.chain()
            .focus()
            .insertContent({
              type: 'text',
              marks: [{ type: 'link', attrs: { href: trimmedUrl } }],
              text: trimmedUrl,
            })
            .run();
        }
      },
      isActive: editor.isActive('link'),
      titleId: RichTextTranslationKeys.richtextLink,
    },
  ];

  const listButtons: ToolbarButton[] = [
    {
      icon: <FormatListNumbered sx={toolbarIconSx} />,
      action: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: editor.isActive('orderedList'),
      titleId: RichTextTranslationKeys.richtextNumberedList,
    },
    {
      icon: <FormatListBulleted sx={toolbarIconSx} />,
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: editor.isActive('bulletList'),
      titleId: RichTextTranslationKeys.richtextBulletList,
    },
  ];

  const breakButton: ToolbarButton = {
    icon: <KeyboardReturn sx={toolbarIconSx} />,
    action: () => editor.chain().focus().setHardBreak().run(),
    isActive: false,
    titleId: RichTextTranslationKeys.richtextLineBreak,
  };

  const groupDivider = <Divider orientation="vertical" flexItem sx={toolbarDividerSx} />;

  return (
    <Box data-rich-text-toolbar='true' sx={toolbarSx(error)}>
      {renderButtons(textFormatButtons)}
      {groupDivider}
      <TextColorButtons editor={editor} disabled={disabled} />
      {RICH_TEXT_VARIANTS[variant].hasBlockTools && (
        <>
          {groupDivider}
          {renderButtons(insertButtons)}
          {groupDivider}
          {renderButtons(listButtons)}
          {groupDivider}
          {renderButtons([breakButton])}
        </>
      )}
    </Box>
  );
};
